import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from "react-datepicker";
import { updateMaterialImport, setUpdateMaterialImportId } from '../../actions/materialImports';
import { getWarehouseOptions, getMaterialOptions, clearMaterialOptions, getSupplierOptions } from '../../actions/material';
import { getFirmOptionsForManagers } from '../../actions/user';
import { usePermission } from '../../hooks/usePermission';
import MaterialImportItemForm from './MaterialImportItemForm';
import { Plus } from 'react-feather';
import { formatDateForSubmission } from '../../utils/dateHelper';

const MaterialImportUpdateForm = ({
    updateMaterialImport,
    setUpdateMaterialImportId,
    getWarehouseOptions,
    getMaterialOptions,
    clearMaterialOptions,
    getFirmOptionsForManagers,
    getSupplierOptions,
    materials,
    materialImportId,
    materialImports,
    suppliers,
    warehouses,
    firms
}) => {
    const { userCan } = usePermission();
    
    const [formData, setFormData] = useState({
        supplier_id: '',
        firm_id: '',
        warehouse_id: '',
        date: new Date(),
        is_for_special_sale: false,
        description: '',
        items: []
    });

    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedFirm, setSelectedFirm] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    
    // Refs to prevent duplicate fetches
    const hasFetchedFirmsRef = useRef(false);
    const hasFetchedSuppliersRef = useRef(false);

    useEffect(() => {
        // Only fetch firm and supplier options once
        if (!hasFetchedFirmsRef.current) {
            hasFetchedFirmsRef.current = true;
            getFirmOptionsForManagers();
        }
        if (!hasFetchedSuppliersRef.current) {
            hasFetchedSuppliersRef.current = true;
            getSupplierOptions();
        }
        
        if (materialImportId) {
            // When editing, fetch warehouses for the specific firm
            const materialImport = materialImports?.find(mi => mi.id === materialImportId);
            if (materialImport?.warehouse?.firm_id) {
                getWarehouseOptions({ 
                    firm_id: materialImport.warehouse.firm_id, 
                    type: 'material' 
                });
                // Load materials for the specific firm and warehouse
                getMaterialOptions({
                    firm_id: materialImport.warehouse.firm_id,
                    warehouse_id: materialImport.warehouse_id
                });
            } else {
                getWarehouseOptions({ type: 'material' });
                clearMaterialOptions(); // Clear materials if no firm/warehouse
            }
        } else {
            getWarehouseOptions({ type: 'material' });
            clearMaterialOptions(); // Clear materials initially
        }
    }, [getMaterialOptions, getWarehouseOptions, getFirmOptionsForManagers, getSupplierOptions, clearMaterialOptions, materialImportId, materialImports]);

    // Filter materials when firm or warehouse changes
    useEffect(() => {
        // Only load materials when both firm and warehouse are selected
        if (selectedFirm?.value && selectedWarehouse?.value) {
            getMaterialOptions({
                firm_id: selectedFirm.value,
                warehouse_id: selectedWarehouse.value
            });
        } else {
            // Clear materials if either firm or warehouse is not selected
            clearMaterialOptions();
        }
    }, [selectedFirm, selectedWarehouse, getMaterialOptions, clearMaterialOptions]);

    // Reset warehouse selection if it's no longer valid
    useEffect(() => {
        if (selectedWarehouse && warehouses.length > 0) {
            // Check if the selected warehouse is still valid for the current firm
            const isValidWarehouse = warehouses.some(w => 
                w.id === selectedWarehouse.value && 
                (!selectedFirm || w.firm_id === selectedFirm.value)
            );
            
            if (!isValidWarehouse) {
                setSelectedWarehouse(null);
            }
        }
    }, [selectedFirm, warehouses, selectedWarehouse]);

    useEffect(() => {
        if (materialImportId && materialImports) {
            const materialImport = materialImports.find(mi => mi.id === materialImportId);
            if (materialImport) {
                // First load materials for this firm and warehouse to ensure options are available
                if (materialImport.warehouse?.firm_id && materialImport.warehouse_id) {
                    getMaterialOptions({
                        firm_id: materialImport.warehouse.firm_id,
                        warehouse_id: materialImport.warehouse_id
                    });
                }
                
                // Short delay to ensure materials are loaded before setting form data
                setTimeout(() => {
                    setFormData({
                        supplier_id: materialImport.supplier_id,
                        firm_id: materialImport.warehouse?.firm_id,
                        warehouse_id: materialImport.warehouse_id,
                        date: new Date(materialImport.date),
                        is_for_special_sale: materialImport.is_for_special_sale,
                        description: materialImport.description || '',
                        items: materialImport.items.map(item => ({
                            id: item.id,
                            material_id: item.material_id,
                            quantity: item.quantity,
                            price: item.price
                        }))
                    });
                    
                    setSelectedSupplier({
                        value: materialImport.supplier_id,
                        label: materialImport.supplier?.name
                    });
                    
                    if (materialImport.warehouse?.firm_id) {
                        setSelectedFirm({
                            value: materialImport.warehouse.firm_id,
                            label: materialImport.warehouse.firm?.name
                        });
                    }
                    
                    setSelectedWarehouse({
                        value: materialImport.warehouse_id,
                        label: materialImport.warehouse?.name
                    });
                }, 100);
            }
        }
    }, [materialImportId, materialImports, getMaterialOptions]);

    const { date, is_for_special_sale, description, items } = formData;

    const onChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const onItemChange = (index, field, value) => {
        const newItems = [...items];
        // Convert quantity and price to numbers with 2 decimal places
        if (field === 'quantity' || field === 'price') {
            value = value === '' ? '' : Number(parseFloat(value).toFixed(2));
        }
        newItems[index] = { ...newItems[index], [field]: value };
        setFormData({ ...formData, items: newItems });
    };

    const addItem = () => {
        setFormData({
            ...formData,
            items: [...items, { material_id: '', quantity: '', price: '' }]
        });
    };

    const removeItem = (index) => {
        if (items.length > 1) {
            const newItems = items.filter((_, i) => i !== index);
            setFormData({ ...formData, items: newItems });
        }
    };



    const onSubmit = e => {
        e.preventDefault();
        // Convert all quantities and prices to numbers with 2 decimal places
        const processedItems = items.map(item => ({
            ...item,
            quantity: Number(parseFloat(item.quantity || 0).toFixed(2)),
            price: Number(parseFloat(item.price || 0).toFixed(2))
        }));
        updateMaterialImport(materialImportId, {
            ...formData,
            items: processedItems,
            supplier_id: selectedSupplier.value,
            // Keep existing firm_id and warehouse_id
            firm_id: formData.firm_id,
            warehouse_id: formData.warehouse_id,
            date: formatDateForSubmission(date)
        });
        handleClose();
    };

    const handleClose = () => {
        setUpdateMaterialImportId(null);
        setFormData({
            supplier_id: '',
            firm_id: '',
            warehouse_id: '',
            date: new Date(),
            is_for_special_sale: false,
            description: '',
            items: []
        });
        setSelectedSupplier(null);
        setSelectedFirm(null);
        setSelectedWarehouse(null);
    };

    // Ensure material IDs are consistent by converting to numbers
    const materialOptions = materials?.map(material => ({
        value: Number(material.id),  // Convert to number to ensure consistent comparison
        label: material.name,
        unit: material.unit,
        unit_alternative: material.unit_alternative
    })) || [];

    const supplierOptions = suppliers?.map(supplier => ({
        value: supplier.id,
        label: supplier.name
    })) || [];

    const firmOptions = firms?.map(firm => ({
        value: firm.id,
        label: firm.name
    })) || [];

    // Don't filter warehouses on frontend, just map what we get from the API
    const warehouseOptions = warehouses.map(warehouse => ({
        value: warehouse.id,
        label: warehouse.name
    }));
    
    // Check permissions and materialImportId after all hooks are called
    if (!userCan('edit material imports') || !materialImportId) {
        return null;
    }

    return (
        <Modal show={!!materialImportId} onHide={handleClose} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Material Import tahrirlash</Modal.Title>
            </Modal.Header>
            <Form onSubmit={onSubmit}>
                <Modal.Body>
                    <div className="row">
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Ta'minotchi</Form.Label>
                                <Select
                                    value={selectedSupplier}
                                    onChange={setSelectedSupplier}
                                    options={supplierOptions}
                                    placeholder="Ta'minotchini tanlang..."
                                    isSearchable={true}
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Firma</Form.Label>
                                <Select
                                    value={selectedFirm}
                                    options={firmOptions}
                                    placeholder="Firmani tanlang..."
                                    isSearchable={true}
                                    isDisabled={true}
                                    className="disabled-select"
                                />
                                <small className="text-muted">Firma o'zgartirib bo'lmaydi</small>
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Ombor</Form.Label>
                                <Select
                                    value={selectedWarehouse}
                                    options={warehouseOptions}
                                    placeholder="Omborni tanlang..."
                                    isSearchable={true}
                                    isDisabled={true}
                                    className="disabled-select"
                                />
                                <small className="text-muted">Ombor o'zgartirib bo'lmaydi</small>
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Sana</Form.Label>
                                <div className="d-flex align-items-center gap-2">
                                    <DatePicker
                                        selected={date}
                                        onChange={date => setFormData({ ...formData, date })}
                                        className="form-control"
                                        wrapperClassName='w-100'
                                        dateFormat="yyyy-MM-dd"
                                    />
                                </div>
                            </Form.Group>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">
                            <Form.Group className="mb-3">
                                <Form.Label>Tavsif</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="description"
                                    value={description}
                                    onChange={onChange}
                                    placeholder="Tavsifni kiriting..."
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-12">
                            <Form.Check
                                type="checkbox"
                                label="Maxsus sotuvga mo'ljallangan"
                                name="is_for_special_sale"
                                checked={is_for_special_sale}
                                onChange={e => setFormData({ ...formData, is_for_special_sale: e.target.checked })}
                            />
                        </div>
                    </div>

                    <div className="border rounded p-3 mb-3">
                        <h5 className="mb-3">Materiallar</h5>
                        {items.map((item, index) => (
                            <MaterialImportItemForm
                                key={index}
                                item={item}
                                index={index}
                                materialOptions={materialOptions}
                                onChange={onItemChange}
                                onRemove={removeItem}
                            />
                        ))}
                        <div className="d-flex justify-content-center">
                            <Button 
                                variant="outline-primary" 
                                onClick={addItem}
                                className="d-flex align-items-center"
                            >
                                <Plus size={16} className="me-2" />
                                Material qo'shish
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Bekor qilish
                    </Button>
                    <Button variant="primary" type="submit">
                        Saqlash
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

MaterialImportUpdateForm.propTypes = {
    updateMaterialImport: PropTypes.func.isRequired,
    setUpdateMaterialImportId: PropTypes.func.isRequired,
    getWarehouseOptions: PropTypes.func.isRequired,
    getMaterialOptions: PropTypes.func.isRequired,
    clearMaterialOptions: PropTypes.func.isRequired,
    getFirmOptionsForManagers: PropTypes.func.isRequired,
    getSupplierOptions: PropTypes.func.isRequired,
    materials: PropTypes.array,
    suppliers: PropTypes.array,
    warehouses: PropTypes.array,
    firms: PropTypes.array,
    materialImportId: PropTypes.number,
    materialImports: PropTypes.array
};

const mapStateToProps = state => ({
    materials: state.material.material_options,
    suppliers: state.material.supplier_options,
    warehouses: state.material.material_warehouses_options,
    firms: state.user.firm_options,
    materialImportId: state.materialImport.updateMaterialImportId,
    materialImports: state.materialImport.materialImports
});

export default connect(mapStateToProps, { 
    updateMaterialImport, 
    setUpdateMaterialImportId, 
    getWarehouseOptions,
    getMaterialOptions,
    clearMaterialOptions,
    getFirmOptionsForManagers,
    getSupplierOptions
})(MaterialImportUpdateForm);
